import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import ReviewCover from "../../assets/Protection/review-cover.png";
import startLifeImg from "../../assets/Protection/start-life-policy.png";
import LifeInsuranceImg from "../../assets/Protection/life-insurance.png";
import IncomeProtectionImg from "../../assets/Protection/income-protection.png";
import MortgageProtectionImg from "../../assets/Protection/Mortgage-Protection.png";
import PensionImg from "../../assets/Protection/Pension.png";
import LifeInsuranceServiceImg from "../../assets/Protection/Tailored-life-protection.png";
import IncomeProtectionServiceImg from "../../assets/Protection/Income-Security.png";
import { Helmet } from "react-helmet-async";
import "./LifeProtection.css";

const LifeProtection: React.FC = () => {
  const protectionInfo = {
    "Life Insurance":
      "Ensure your family is financially secure in the event of an unexpected loss with tailored life cover options.",
    "Income Protection":
      "Secure your income against illness, injury, or disability to maintain financial stability and peace of mind.",
    "Mortgage Protection":
      "Ensure your mortgage is covered, even in challenging circumstances, providing financial security for your family.",
    "Pension Term Assurance":
      "Protect your retirement savings with tax-efficient pension term assurance that provides decreasing life cover aligned with your pension growth.",
    "Review My Current Cover":
      "Review My Current Life Cover helps you assess your existing policy to ensure it meets your needs and provides adequate protection for your loved ones.",
    "Start a New Life Policy":
      "Ready to secure your future? Use our Start a New Life Policy feature to protect what matters most today!",
  };
  const navigate = useNavigate();

  const handleRedirect = () => {
    navigate("/contact/");
    setTimeout(() => {
      const element = document.getElementById("get-in-touch");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
      },
    },
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } },
  };

  const [heroRef, heroInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <div className="life-protection">
      <Helmet>
        <title>
          Life & Protection - Apollo Financial Services | Apollo Financial
          Services Ireland
        </title>
        <meta
          name="description"
          content="Comprehensive life insurance, income protection and mortgage cover in Ireland. Get expert advice on protecting your family's financial future."
        />
        <meta
          name="keywords"
          content="investment advice for inheritance money, financial planning after inheritance, budgeting advice for high-net-worth individuals, wealth management for tech professionals, financial advisor for startup founders, succession planning for entrepreneurs, financial guidance for family businesses, financial security for artists, estate planning for digital nomads, investment advice for crypto investors, passive income financial planning, financial freedom planning Ireland, how to plan early retirement in Ireland, transitioning from PAYE to self-employed finance tips, tax benefits of pension contributions, financial planning for newly divorced, cohabiting couples financial planning, joint finances advice Ireland, investment options for couples, how to manage shared debt in relationships, best online financial advisor Ireland, virtual financial planner Ireland, online pension calculator Ireland, how to book a financial consultation online, do I need a financial advisor quiz, free financial planning consultation Ireland, free budget planner Ireland, best financial planning app Ireland, wealth tracking tools Ireland, DIY financial planning guide, how to save for retirement calculator, mortgage repayment calculator Ireland, tax rebate calculator Ireland, how to file tax returns Ireland, online wealth management platform, compare pension plans Ireland, robo-advisors vs. financial planners, is financial planning worth it, how to start investing in Ireland, best personal finance blogs Ireland, tips for managing personal budget, where to invest money in 2025, guide to investment funds in Ireland, how to choose pension provider Ireland, can I retire early in Ireland, best retirement savings account Ireland, online tax planner Ireland, saving for a mortgage Ireland, should I pay off debt or invest, how to avoid lifestyle inflation"
        />
        <meta
          property="og:title"
          content="Life & Protection - Apollo Financial Services"
        />
        <meta
          property="og:description"
          content="Comprehensive life insurance, income protection and mortgage cover in Ireland. Get expert advice on protecting your family's financial future."
        />
        <meta
          property="og:url"
          content="https://www.apollofinancial.ie/life-protection/"
        />
        <meta property="og:type" content="website" />
        <link
          rel="canonical"
          href="https://www.apollofinancial.ie/life-protection/"
        />
      </Helmet>

      {/* Add Structured Data */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "InsuranceAgency",
            "name": "Apollo Financial Services",
            "description": "Life and protection - Apollo Financial Services",
            "url": "https://www.apollofinancial.ie/life-protection/",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Thomastown House, Thomastown",
              "addressLocality": "Duleek",
              "addressRegion": "Co. Meath",
              "postalCode": "A92 X362",
              "addressCountry": "IE"
            },
            "offers": {
              "@type": "Offer",
              "name": "Life Protection Services",
              "description": "Comprehensive life insurance, income protection and mortgage cover solutions",
              "url": "https://www.apollofinancial.ie/life-protection/"
            },
            "provider": {
              "@type": "Organization",
              "name": "Apollo Financial Services",
              "sameAs": [
                "https://www.facebook.com/ApolloFinancialServicesLtd",
                "https://www.linkedin.com/company/apollo-financial-services-limited/"
              ]
            }
          }
        `}
      </script>
      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        className="protection-hero"
        initial="hidden"
        animate={heroInView ? "visible" : "hidden"}
        variants={fadeInVariants}
      >
        <div className="hero-overlay"></div>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
        >
          <motion.h1 variants={itemVariants}>Life & Protection</motion.h1>
          <motion.p variants={itemVariants}>
            Comprehensive solutions to secure your family's financial future.
          </motion.p>
        </motion.div>
      </motion.section>

      {/* Call to Action Section */}
      <div className="protection-actions">
        {[
          {
            title: "Review My Current Cover",
            description: protectionInfo["Review My Current Cover"],
            img: ReviewCover,
          },
          {
            title: "Start a New Life Policy",
            description: protectionInfo["Start a New Life Policy"],
            img: startLifeImg,
          },
          {
            title: "Life Insurance",
            description: protectionInfo["Life Insurance"],
            img: LifeInsuranceImg,
          },
          {
            title: "Income Protection",
            description: protectionInfo["Income Protection"],
            img: IncomeProtectionImg,
          },
          {
            title: "Mortgage Protection",
            description: protectionInfo["Mortgage Protection"],
            img: MortgageProtectionImg,
          },
          {
            title: "Pension Term Assurance",
            description: protectionInfo["Pension Term Assurance"],
            img: PensionImg,
          },
        ].map((item, index) => {
          return (
            <div
              className={`protection-section ${
                index % 2 === 0 ? "text-left" : "text-right"
              }`}
              key={index}
            >
              <div className="protection-content">
                <h2>{item.title}</h2>
                <p>{item.description}</p>
                <button className="protection-btn" onClick={handleRedirect}>
                  Find Out More →
                </button>
              </div>
              <div className="protection-image">
                <img src={item.img} alt={item.title} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Why Protection Matters Section */}
      <section className="protection-content-section">
        <div className="protection-intro">
          <h2>Why Life Protection Matters</h2>
          <p>
            Protect what matters most with comprehensive life insurance,
            mortgage protection, and income protection plans. Our
            advisors ensure you receive the best options at the most competitive
            rates.
          </p>
        </div>

        <div className="protection-services">
          <div className="protection-service-item">
            <img
              src={LifeInsuranceServiceImg}
              alt="Custom Life Cover"
              className="protection-service-image"
            />
            <div className="protection-service-content">
              <h3>Tailored Life Protection</h3>
              <p>
                ✔ We customize your life cover to suit your unique financial and
                family needs.
              </p>
            </div>
          </div>

          <div className="protection-service-item protection-reverse">
            <img
              src={IncomeProtectionServiceImg}
              alt="Income Security"
              className="protection-service-image"
            />
            <div className="protection-service-content">
              <h3>Income Security</h3>
              <p>
                ✔ Protect your income against unforeseen circumstances, ensuring
                stability for you and your family.
              </p>
            </div>
          </div>

          <div className="protection-service-item">
            <img
              src={PensionImg}
              alt="Critical Illness Protection"
              className="protection-service-image"
            />
            <div className="protection-service-content">
              <h3>Pension Term Assurance</h3>
              <p>✔ Secure your retirement with pension term assurance plans.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="protection-why">
        <h2>Why Choose Our Life Protection Services?</h2>
        <div className="protection-features">
          <div className="protection-feature-item">
            <h3>Trusted Expertise</h3>
            <p>
              We handle complex cases and liaise with insurers to secure the
              most suitable coverage available.
            </p>
          </div>
          <div className="protection-feature-item">
            <h3>Smart Savings</h3>
            <p>
              We work with trusted providers to secure the best possible
              pricing, helping you reduce costs.
            </p>
          </div>
          <div className="protection-feature-item">
            <h3>Hassle-Free Process</h3>
            <p>
              We take care of all the paperwork, ensuring a seamless and
              stress-free experience.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LifeProtection;
